import { auth } from '../auth/auth';
import { getErrorMessage } from '../util/errorHandler';
import { getConfig } from '../config/config';
import { logError, logInfo, reportUserError } from '../logging/logging';

export interface IApiResponse<TResult> {
    success: boolean;
    result?: TResult;
}

const ContentTypeKey = 'content-type';
const defaultHeaders: { [key: string]: string } = {};
defaultHeaders[ContentTypeKey] = 'application/json';
const GenericErrorMessage = 'An unknown error occurred. Please try again or contact your administrator.';

const makeUri = (apiBaseUrl: string, path: string) => `${apiBaseUrl}/${path}`;

const makeHeaders = (customHeaders: Record<string, string>, isItMultipartForm: boolean): Record<string, string> => {
    const headers = {
        ...defaultHeaders,
        ...customHeaders,
    };
    if (isItMultipartForm) {
        delete headers[ContentTypeKey];
    }
    return headers;
};

const formatData = <T>(data: T, files: File[] | undefined) => {
    const form = data ? JSON.stringify(data) : undefined;
    const isItMultipartForm = !!files;
    if (!isItMultipartForm) {
        return { form, isItMultipartForm };
    }

    const formData = new FormData();
    files!.map((file) => formData.append('files', file));
    if (form) {
        formData.append('form', form);
    }
    return { form: formData, isItMultipartForm };
};

interface IRequestParams<TData = unknown, TResult = unknown> {
    path: string;
    scopes: string[];
    method: string;
    data?: TData;
    files?: File[];
    raiseErrorOnFailure: boolean;
    readData: (response: Response) => Promise<TResult>;
    signal?: AbortSignal;
}

const readJsonData = async (response: Response) => {
    const json = await response.text();
    return json ? JSON.parse(json) : undefined;
};

const readBinaryData = async (response: Response) => await response.blob();

const sendRequest = async <TResult>(params: IRequestParams<unknown, TResult>, accessToken?: string): Promise<IApiResponse<TResult>> => {
    try {
        if (accessToken) {
            logInfo('Access Token available from API - ' + accessToken);
            return sendRequestWithAccessToken<TResult>(accessToken, params);
        } else {
            logInfo('Getting Access Token from API for scope - ' + params.scopes);
            return auth.getAccessToken(params.scopes).then(async (token) => {
                logInfo('Generated Access Token from API is - ' + token);
                return sendRequestWithAccessToken<TResult>(token, params);
            });
        }
    } catch (e: unknown) {
        if (params.signal?.aborted || (typeof DOMException !== 'undefined' && e instanceof DOMException && e.name === 'AbortError')) {
            return { success: false };
        }
        logError(getErrorMessage(e));
        if (params.raiseErrorOnFailure) {
            logError(`Error details: ${getErrorMessage(e)}`);
            reportUserError(GenericErrorMessage, params.path);
        }
        return { success: false };
    }
};

const sendRequestWithAccessToken = async <TResult>(accessToken: string, params: IRequestParams<unknown, TResult>): Promise<IApiResponse<TResult>> => {
    const { form, isItMultipartForm } = formatData(params.data, params.files);
    const headers = makeHeaders(
        {
            authorization: `Bearer ${accessToken}`,
        },
        isItMultipartForm,
    );
    const response = await fetch(params.path, {
        body: form,
        method: params.method,
        cache: 'no-cache',
        headers,
        signal: params.signal,
    });

    const obj = await params.readData(response);
    const responseOK = isResponseOk(response);

    return {
        success: responseOK,
        result: obj,
    };
};

// Some sites like ServiceNow have their own polyfill for fetch which does
// not include the OK property, so use the status code instead as it's more
// reliable.
const isResponseOk = (response: Response) => {
    return response.status >= 200 && response.status <= 299;
};

const sendApiRequest = <TData, TResult>(
    path: string,
    method: string,
    data: TData,
    files: File[] | undefined,
    accessToken: string | undefined,
    signal?: AbortSignal,
): Promise<IApiResponse<TResult>> =>
    sendRequest(
        {
            path: makeUri(getConfig().apiBaseUrl, path),
            scopes: auth.getApplicationScopes(),
            method,
            data,
            files,
            raiseErrorOnFailure: true,
            readData: readJsonData,
            signal,
        },
        accessToken,
    );

export const Api = {
    get: async <TResult>(path: string, signal?: AbortSignal, accessToken?: string): Promise<IApiResponse<TResult>> =>
        sendApiRequest<undefined, TResult>(path, 'GET', undefined, undefined, accessToken, signal),
    post: async <TData, TResult>(
        path: string,
        data: TData,
        signal?: AbortSignal,
        files?: File[],
        accessToken?: string,
    ): Promise<IApiResponse<TResult>> => sendApiRequest<TData, TResult>(path, 'POST', data, files, accessToken, signal),
    put: async <TData, TResult>(
        path: string,
        data: TData,
        signal?: AbortSignal,
        files?: File[],
        accessToken?: string,
    ): Promise<IApiResponse<TResult>> => sendApiRequest<TData, TResult>(path, 'PUT', data, files, accessToken, signal),
    delete: async <TData, TResult>(path: string, data: TData, signal?: AbortSignal, accessToken?: string): Promise<IApiResponse<TResult>> =>
        sendApiRequest<TData, TResult>(path, 'DELETE', data, undefined, accessToken, signal),
};

const graphApiUrlBase = 'https://graph.microsoft.com/v1.0';

export const GraphApi = {
    getUserPhoto: async (signal?: AbortSignal): Promise<any> => {
        return sendRequest({
            path: makeUri(graphApiUrlBase, 'me/photos/48x48/$value'),
            scopes: ['user.read'],
            method: 'GET',
            raiseErrorOnFailure: false,
            readData: readBinaryData,
            signal,
        });
    },
};
